import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, List, Package } from 'lucide-react';
import { Input } from './ui/Input';

interface RaiseDisputeModalProps {
  discrepancy: {
    awb: string;
    mentionedDeadWeight: number;
    mentionedVolumetricWeight: number;
    chargedDeadWeight: number;
    chargedVolumetricWeight: number;
    excessWeight: number;
    excessCharges: number;
    orderId: string;
    sku: string;
    qty: number;
    productName: string;
    isBulkDispute?: boolean;
    bulkCount?: number;
    bulkItems?: Array<{
      awb: string;
      productName: string;
      excessCharges: number;
      mentionedDeadWeight: number;
      mentionedVolumetricWeight: number;
      chargedDeadWeight: number;
      chargedVolumetricWeight: number;
      excessWeight: number;
    }>;
  };
  isBulk?: boolean;
}

interface UploadedImage {
  id: string;
  url: string;
  label: string;
}

export default function RaiseDisputeModal({
  discrepancy,
  isBulk = false,
}: RaiseDisputeModalProps) {
  const [productName, setProductName] = useState(discrepancy.productName);
  const [productUrl, setProductUrl] = useState('');
  const [remark, setRemark] = useState('');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [showBulkSummary, setShowBulkSummary] = useState(true);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const imageCategories = [
    { id: 'package-length', label: 'Package Length Image', required: true },
    { id: 'package-width', label: 'Package Width Image', required: true },
    { id: 'package-height', label: 'Package Height Image', required: true },
    {
      id: 'weighing-machine',
      label: 'Shipment on weighing machine Image',
      required: true,
    },
    { id: 'package-label', label: 'Package With label Image', required: true },
  ];

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    categoryId: string,
    label: string
  ) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      const imageUrl = URL.createObjectURL(file);

      // Remove existing image for this category if any
      setUploadedImages((prev) => {
        const filtered = prev.filter((img) => img.label !== label);
        return [...filtered, { id: categoryId, url: imageUrl, label }];
      });
    }
  };

  const removeImage = (categoryId: string) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== categoryId));
    // Reset the file input
    const index = imageCategories.findIndex((cat) => cat.id === categoryId);
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index]!.value = '';
    }
  };

  const getUploadedImage = (categoryId: string) => {
    return uploadedImages.find((img) => img.id === categoryId);
  };

  const handleSubmit = () => {
    // Handle dispute submission
    console.log('Submit dispute:', {
      discrepancy,
      productName,
      productUrl,
      remark,
      images: uploadedImages,
    });
  };

  // If it's a bulk dispute, render bulk view first
  if (isBulk && discrepancy.bulkCount && discrepancy.bulkCount > 1) {
    return (
      <div className="max-h-[90vh] overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-text flex items-center gap-2">
            <List size={24} className="text-teal" />
            Raise Bulk Dispute for {discrepancy.bulkCount} Items
          </h2>
          <p className="text-text1 mt-1">
            You are raising a dispute for {discrepancy.bulkCount} discrepancies
          </p>
        </div>

        {/* Bulk Summary Section */}
        {showBulkSummary && (
          <div className="mb-8 border rounded-lg p-6 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Package size={20} className="text-teal" />
                Items Summary
              </h3>
              <button
                onClick={() => setShowBulkSummary(false)}
                className="text-sm text-teal hover:text-teal-dark font-medium"
              >
                Hide Details
              </button>
            </div>

            {/* Summary of items */}
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {discrepancy.bulkItems?.map((item, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-lg">AWB: {item.awb}</div>
                      <div className="text-sm text-text1">
                        {item.productName}
                      </div>
                    </div>
                    <div className="text-red-600 font-bold text-lg">
                      ₹{item.excessCharges}
                    </div>
                  </div>

                  {/* Weight details for each item */}
                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div>
                      <div className="font-medium text-gray-600">
                        Mentioned Weight
                      </div>
                      <div>Dead: {item.mentionedDeadWeight} Kg</div>
                      <div>Vol.: {item.mentionedVolumetricWeight} Kg</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-600">
                        Charged Weight
                      </div>
                      <div>Dead: {item.chargedDeadWeight} Kg</div>
                      <div>Vol.: {item.chargedVolumetricWeight} Kg</div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Excess Weight:</span>{' '}
                    {item.excessWeight} Kg
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-6 pt-6 border-t">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="text-center p-3 bg-white rounded-lg border">
                  <div className="text-sm text-gray-600 font-medium">
                    Total Excess Weight
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {discrepancy.excessWeight} Kg
                  </div>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border">
                  <div className="text-sm text-gray-600 font-medium">
                    Total Excess Charges
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    ₹{discrepancy.excessCharges}
                  </div>
                </div>
              </div>

              <div className="flex justify-between font-bold text-lg pt-4">
                <span>Total Items:</span>
                <span className="text-teal">{discrepancy.bulkCount} items</span>
              </div>
            </div>
          </div>
        )}

        {!showBulkSummary && (
          <div className="mb-6 text-center">
            <button
              onClick={() => setShowBulkSummary(true)}
              className="text-teal hover:text-teal-dark font-medium flex items-center gap-2 mx-auto"
            >
              <List size={16} />
              Show {discrepancy.bulkCount} Items Summary
            </button>
          </div>
        )}

        {/* Weight Comparison Table for Bulk */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">
              Total Mentioned Weight
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-semibold">Dead Weight:</span>{' '}
                {discrepancy.mentionedDeadWeight} Kg
              </div>
              <div className="text-sm">
                <span className="font-semibold">Volumetric Weight:</span>{' '}
                {discrepancy.mentionedVolumetricWeight} Kg
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">Total Charged Weight</h3>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-semibold">Dead Weight:</span>{' '}
                {discrepancy.chargedDeadWeight} Kg
              </div>
              <div className="text-sm">
                <span className="font-semibold">Volumetric Weight:</span>{' '}
                {discrepancy.chargedVolumetricWeight} Kg
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3">
              Total Excess Weight & Charge
            </h3>
            <div className="space-y-2">
              <div className="text-sm font-medium">
                <span className="font-semibold">Excess Weight:</span>{' '}
                {discrepancy.excessWeight} Kg
              </div>
              <div className="text-sm font-medium text-red-600">
                <span className="font-semibold">Excess Charges:</span> ₹
                {discrepancy.excessCharges}
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Form */}
        <div className="border-t pt-6 mb-6">
          <h3 className="font-semibold text-lg mb-4">Add Product Details</h3>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Input
                value={productName}
                label="Product Names (Multiple)"
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Enter product names"
                required
              />
            </div>

            <div>
              <Input
                value={productUrl}
                label="Product URL (optional)"
                onChange={(e) => setProductUrl(e.target.value)}
                placeholder="Enter product URL"
              />
            </div>

            <div>
              <Input
                value={remark}
                label="Remark for All Items"
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Enter remarks for all items"
                required
              />
            </div>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg">
            <div className="text-base">
              <div>
                <span className="font-semibold">Total Items:</span>{' '}
                {discrepancy.bulkCount}
              </div>
              <div>
                <span className="font-semibold">Total Quantity:</span>{' '}
                {discrepancy.qty}
              </div>
              <div className="text-sm text-text1 mt-2">
                This dispute includes {discrepancy.bulkCount} shipments. Please
                ensure all images are uploaded for each shipment type.
              </div>
            </div>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-4">Add Product Images</h3>

          <div className="text-sm text-text1 mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <span className="font-medium">Note:</span> Please upload images for
            each shipment type. You may need multiple images if shipments have
            different packaging.
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {imageCategories.map((item, index) => {
              const uploadedImage = getUploadedImage(item.id);

              return (
                <div
                  key={item.id}
                  className={`border-2 rounded-lg px-1 py-4 text-center transition-colors relative min-h-[160px] flex flex-col ${
                    uploadedImage
                      ? 'border-teal bg-teal/5'
                      : 'border-dashed border-gray-300 hover:border-teal'
                  }`}
                >
                  <input
                    type="file"
                    id={`upload-${item.id}`}
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.svg"
                    onChange={(e) => handleImageUpload(e, item.id, item.label)}
                    multiple={true}
                  />

                  <label
                    htmlFor={`upload-${item.id}`}
                    className="cursor-pointer flex-1 flex flex-col"
                  >
                    {uploadedImage ? (
                      <>
                        {/* Image Preview */}
                        <div className="relative flex-1 mb-2">
                          <img
                            src={uploadedImage.url}
                            alt={item.label}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          {/* Remove Overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeImage(item.id);
                              }}
                              className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-teal">
                          {item.label}
                        </div>
                        <div className="text-xs text-text1 mt-1">
                          Click to change image(s)
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 flex items-center justify-center">
                          <Upload
                            className="mx-auto mb-2 text-gray-400"
                            size={32}
                          />
                        </div>
                        <div className="text-sm font-medium">{item.label}</div>
                        <div className="text-xs text-text1 mt-1">
                          Upload Image (jpg, png, svg, jpeg)
                        </div>
                        <div className="text-xs text-blue-500 mt-1">
                          Multiple files allowed
                        </div>
                        {item.required && (
                          <div className="text-xs text-red-500 mt-1">
                            *Required
                          </div>
                        )}
                      </>
                    )}
                  </label>

                  {/* Remove button for uploaded images */}
                  {uploadedImage && (
                    <button
                      type="button"
                      onClick={() => removeImage(item.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      title="Remove image"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary of uploaded images */}
          {uploadedImages.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <ImageIcon size={18} className="text-teal" />
                Uploaded Images ({uploadedImages.length}/
                {imageCategories.length})
              </h4>
              <div className="text-sm text-text1">
                {uploadedImages.map((img) => (
                  <div key={img.id} className="flex items-center gap-2 py-1">
                    <span className="w-2 h-2 rounded-full bg-teal"></span>
                    {img.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-2.5 bg-teal text-white rounded-lg hover:bg-teal-dark transition-colors font-medium"
          >
            Submit Bulk Dispute ({discrepancy.bulkCount} items)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className=" max-h-[90vh] overflow-y-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-text">
          Raise Dispute for AWB:{' '}
          <span className="text-teal">{discrepancy.awb}</span>
        </h2>
        <p className="text-text1 mt-1">Weight Discrepancy Details</p>
      </div>

      {/* Weight Comparison Table */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3">Mentioned Weight</h3>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-semibold">Dead Weight:</span>{' '}
              {discrepancy.mentionedDeadWeight} Kg
            </div>
            <div className="text-sm">
              <span className="font-semibold">Volumetric Weight:</span>{' '}
              {discrepancy.mentionedVolumetricWeight} Kg
            </div>
            <div className="text-sm">(10x10x10)</div>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3">Charged Weight</h3>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-semibold">Dead Weight:</span>{' '}
              {discrepancy.chargedDeadWeight} Kg
            </div>
            <div className="text-sm">
              <span className="font-semibold">Volumetric Weight:</span>{' '}
              {discrepancy.chargedVolumetricWeight} Kg
            </div>
            <div className="text-sm">(10x10x10)</div>
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-3">Excess Weight & Charge</h3>
          <div className="space-y-2">
            <div className="text-sm font-medium">
              <span className="font-semibold">Excess Weight:</span>{' '}
              {discrepancy.excessWeight} Kg
            </div>
            <div className="text-sm font-medium text-red-600">
              <span className="font-semibold">Excess Charges:</span> ₹
              {discrepancy.excessCharges}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-6 mb-6">
        <h3 className="font-semibold text-lg mb-4">Add Product Details</h3>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <Input
              value={productName}
              label="Product Name"
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Product Name"
              required
            />
          </div>

          <div>
            <Input
              value={productUrl}
              label="Product URL (optional)"
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder="Enter product URL"
            />
          </div>

          <div>
            <Input
              value={remark}
              label="Remark"
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter remarks"
              required
            />
          </div>
        </div>

        <div className="bg-gray-100 p-4 rounded-lg">
          <div className="text-base">
            <div>
              <span className="font-semibold">Order ID:</span>{' '}
              {discrepancy.orderId}
            </div>
            <div>
              <span className="font-semibold">SKU:</span> {discrepancy.sku}
            </div>
            <div>
              <span className="font-semibold">Qty:</span> {discrepancy.qty}
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Section */}
      <div className="border-t pt-6">
        <h3 className="font-semibold text-lg mb-4">Add Product Images</h3>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {imageCategories.map((item, index) => {
            const uploadedImage = getUploadedImage(item.id);

            return (
              <div
                key={item.id}
                className={`border-2 rounded-lg px-1 py-4 text-center transition-colors relative min-h-[160px] flex flex-col ${
                  uploadedImage
                    ? 'border-teal bg-teal/5'
                    : 'border-dashed border-gray-300 hover:border-teal'
                }`}
              >
                <input
                  type="file"
                  id={`upload-${item.id}`}
                  ref={(el) => (fileInputRefs.current[index] = el)}
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.svg"
                  onChange={(e) => handleImageUpload(e, item.id, item.label)}
                />

                <label
                  htmlFor={`upload-${item.id}`}
                  className="cursor-pointer flex-1 flex flex-col"
                >
                  {uploadedImage ? (
                    <>
                      {/* Image Preview */}
                      <div className="relative flex-1 mb-2">
                        <img
                          src={uploadedImage.url}
                          alt={item.label}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        {/* Remove Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeImage(item.id);
                            }}
                            className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-teal">
                        {item.label}
                      </div>
                      <div className="text-xs text-text1 mt-1">
                        Click to change image
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 flex items-center justify-center">
                        <Upload
                          className="mx-auto mb-2 text-gray-400"
                          size={32}
                        />
                      </div>
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="text-xs text-text1 mt-1">
                        Upload Image (jpg, png, svg, jpeg)
                      </div>
                      {item.required && (
                        <div className="text-xs text-red-500 mt-1">
                          *Required
                        </div>
                      )}
                    </>
                  )}
                </label>

                {/* Remove button for uploaded images (always visible alternative) */}
                {uploadedImage && (
                  <button
                    type="button"
                    onClick={() => removeImage(item.id)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    title="Remove image"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary of uploaded images */}
        {uploadedImages.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <ImageIcon size={18} className="text-teal" />
              Uploaded Images ({uploadedImages.length}/{imageCategories.length})
            </h4>
            <div className="text-sm text-text1">
              {uploadedImages.map((img) => (
                <div key={img.id} className="flex items-center gap-2 py-1">
                  <span className="w-2 h-2 rounded-full bg-teal"></span>
                  {img.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-3 mt-8 pt-6 border-t sticky bottom-0 bg-white">
        <button
          type="button"
          className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-6 py-2.5 bg-teal text-white rounded-lg hover:bg-teal-dark transition-colors font-medium"
        >
          Submit Dispute
        </button>
      </div>
    </div>
  );
}
